import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { FaSave, FaTimes } from 'react-icons/fa';
import PrisepsService from '../../services/PrisepsService';
import { toast } from 'react-toastify';

const PrisepForm = ({ show, onHide, onSave, prisep }) => {
  const [formData, setFormData] = useState({
    name: '',
    davlat_raqam: '',
    tex_passport_raqam: '',
    rang: '',
    izoh: ''
  });
  
  const [transportTypes, setTransportTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Load transport types
  useEffect(() => {
    const loadTransportTypes = async () => {
      try {
        const response = await PrisepsService.getTransportRoles();
        if (response.data) {
          setTransportTypes(response.data);
          
          // If editing, find and set the selected type
          if (prisep && prisep.tur_id) {
            const type = response.data.find(t => t.id === prisep.tur_id);
            if (type) {
              setSelectedType({
                value: type.id,
                label: type.name
              });
            }
          }
        }
      } catch (error) {
        console.error('Error loading transport types:', error);
        toast.error('Transport turlarini yuklashda xatolik yuz berdi');
      }
    };
    
    loadTransportTypes();
  }, [prisep]);

  // Initialize form when component mounts or when prisep prop changes
  useEffect(() => {
    if (prisep) {
      setIsEditMode(true);
      setFormData({
        name: prisep.name || '',
        davlat_raqam: prisep.davlat_raqam || '',
        tex_passport_raqam: prisep.tex_passport_raqam || '',
        rang: prisep.rang || '',
        izoh: prisep.izoh || ''
      });
    } else {
      setIsEditMode(false);
      setFormData({
        name: '',
        davlat_raqam: '',
        tex_passport_raqam: '',
        rang: '',
        izoh: ''
      });
      setSelectedType(null);
    }
    setErrors({});
  }, [prisep]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleTypeChange = (selectedOption) => {
    setSelectedType(selectedOption);
    if (errors.tur_id) {
      setErrors(prev => ({
        ...prev,
        tur_id: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // if (!formData.name) newErrors.name = 'Nomi kiritilmagan';
    if (!formData.davlat_raqam) newErrors.davlat_raqam = 'Davlat raqami kiritilmagan';
    if (!formData.tex_passport_raqam) newErrors.tex_passport_raqam = 'Texnik pasport raqami kiritilmagan';
    if (!selectedType) newErrors.tur_id = 'Transport turi tanlanmagan';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const dataToSend = {
        ...formData,
        tur_id: selectedType.value
      };
      
      const response = await PrisepsService.createPrisep(dataToSend);
      
      if (response.success) {
        toast.success('Prisep muvaffaqiyatli saqlandi');
        onSave(response.data);
        onHide();
      } else {
        // Handle server-side validation errors
        if (response.errors) {
          const serverErrors = {};
          Object.entries(response.errors).forEach(([field, messages]) => {
            serverErrors[field] = Array.isArray(messages) ? messages[0] : messages;
          });
          setErrors(serverErrors);
        } else {
          toast.error(response.message || 'Xatolik yuz berdi');
        }
      }
    } catch (error) {
      console.error('Error saving prisep:', error);
      toast.error(error.message || 'Prisepni saqlashda xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered style={{ zIndex: 9999 }}>
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? 'Prisepni tahrirlash' : 'Yangi prisep qo\'shish'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formName">
                <Form.Label>Nomi</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  isInvalid={!!errors.name}
                  placeholder="Prisep nomini kiriting"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formType">
                <Form.Label>Transport turi <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={selectedType?.value || ''}
                  onChange={(e) => {
                    const selected = transportTypes.find(t => t.id === parseInt(e.target.value));
                    handleTypeChange(selected ? { value: selected.id, label: selected.name } : null);
                  }}
                  isInvalid={!!errors.tur_id}
                >
                  <option value="">Transport turini tanlang</option>
                  {transportTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.tur_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formDavlatRaqam">
                <Form.Label>Davlat raqami <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="davlat_raqam"
                  value={formData.davlat_raqam}
                  onChange={handleChange}
                  isInvalid={!!errors.davlat_raqam}
                  placeholder="01A123BC"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.davlat_raqam}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formTexPassport">
                <Form.Label>Texnik pasport raqami <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="tex_passport_raqam"
                  value={formData.tex_passport_raqam}
                  onChange={handleChange}
                  isInvalid={!!errors.tex_passport_raqam}
                  placeholder="AAB1234567"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.tex_passport_raqam}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formRang">
                <Form.Label>Rangi</Form.Label>
                <Form.Control
                  type="text"
                  name="rang"
                  value={formData.rang}
                  onChange={handleChange}
                  placeholder="Masalan: Qora"
                />
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formIzoh">
                <Form.Label>Izoh</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={1}
                  name="izoh"
                  value={formData.izoh}
                  onChange={handleChange}
                  placeholder="Qo'shimcha ma'lumotlar..."
                />
              </Form.Group>
            </Col>
          </Row>
          
          {Object.keys(errors).length > 0 && (
            <Alert variant="danger" className="mt-3">
              Iltimos, barcha majburiy maydonlarni to'ldiring.
            </Alert>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            <FaTimes className="me-1" /> Bekor qilish
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner as="span" size="sm" animation="border" role="status" aria-hidden="true" className="me-1" />
                Saqlanmoqda...
              </>
            ) : (
              <>
                <FaSave className="me-1" /> Saqlash
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PrisepForm;
